import React, { useState, useEffect, useRef } from 'react';
import './App.css';

import { FEEDS, POLL_MS, API_BASE } from './constants';
import useFeed from './hooks/useFeed';
import useAlert from './hooks/useAlert';

import Navbar          from './components/Navbar';
import TabBar          from './components/TabBar';
import Ticker          from './components/Ticker';
import SignalCard      from './components/SignalCard';
import StatsRow        from './components/StatsRow';
import ActiveSignals   from './components/ActiveSignals';
import FeaturedDecision from './components/FeaturedDecision';
import HistoryTable    from './components/HistoryTable';
import RegimePanel     from './components/RegimePanel';
import PerSystemPanel  from './components/PerSystemPanel';
import Toast           from './components/Toast';
import AIReview        from './pages/AIReview';
import KunbiMonitor    from './pages/KunbiMonitor';
import ImperialMonitor from './pages/ImperialMonitor';
import Tier1Monitor    from './pages/Tier1Monitor';
import WuraMonitor     from './pages/WuraMonitor';
import IyipadaMonitor  from './pages/IyipadaMonitor';
import OsupaMonitor    from './pages/OsupaMonitor';
import OsupaShortMonitor from './pages/OsupaShortMonitor';
import AnyanwuMonitor  from './pages/AnyanwuMonitor';
import IyanuMonitor    from './pages/IyanuMonitor';
import JuyiMonitor     from './pages/JuyiMonitor';
import IsaleMonitor   from './pages/IsaleMonitor';
import NuitMonitor     from './pages/NuitMonitor';
import SiriusMonitor   from './pages/SiriusMonitor';
import RigelMonitor    from './pages/RigelMonitor';
import MarsMonitor     from './pages/MarsMonitor';
import IsinmiMonitor   from './pages/IsinmiMonitor';
import SaturnMonitor   from './pages/SaturnMonitor';
import VenusMonitor    from './pages/VenusMonitor';
import MansaMonitor    from './pages/MansaMonitor';
import SequencingMap   from './pages/SequencingMap';
// Tier 2 / Tier 5 Monitors removed 2026-06-16 (tiers removed from the app).
import { buildTriggerIndex, resolveTrigger } from './utils/algoResolver';
import useTier1TrailAlerts from './hooks/useTier1TrailAlerts';
import SystemHealth    from './pages/SystemHealth';
import OpenPositions   from './pages/OpenPositions';
import Backtest        from './pages/Backtest';
import SignalAudit     from './pages/SignalAudit';

// Slot-leader status (doc-verified, Mansa ES-desk kit v1). Self-contained pure helper
// so both the render body (feed/Decision Log) and the toast-fire effect can call it.
//   yield  = this book may trade only when the leader SKIPPED (leader HELD → blocked)
//   mirror = this book takes its direction from the leader that HELD
// ok = "good for THIS book" (leader skipped for yield, leader took for mirror).
const SLOT_DEPS = {
  RIGEL:  [['SIRIUS', 'yield']],
  MARS:   [['SIRIUS', 'yield'], ['RIGEL', 'yield'], ['ANYANWU', 'mirror']],
  ISINMI: [['ANYANWU', 'yield'], ['JUYI', 'yield'], ['SIRIUS', 'mirror'], ['RIGEL', 'mirror']],
  VENUS:  [['SATURN', 'yield']],
};
function slotLeadersFrom(d, all) {
  const sys = x => (x.system_id || x.system || x.strategy || '').toString().toUpperCase();
  const deps = SLOT_DEPS[sys(d)];
  if (!deps || !Array.isArray(all)) return null;
  const day = x => (x.signal_ts || x.session_date || x.decided_at || '').toString().slice(0, 10);
  const took = x => (x.action === 'TAKE' || x.decision === 'TRADE' || x.decision === 'HOLD' || x.action_label === 'TRADE');
  const hhmm = ts => (ts || '').toString().slice(11, 16);
  const dd = day(d);
  const state = {};
  all.forEach(x => {
    if (day(x) !== dd) return;
    const s = sys(x), t = took(x), cur = state[s];
    if (t && (!cur || !cur.took)) state[s] = { took: true, side: (x.direction || x.side || '').toString().toUpperCase(), at: hhmm(x.signal_ts) };
    else if (!cur) state[s] = { took: false, side: null, at: hhmm(x.signal_ts) };
  });
  return deps.map(([leader, rel]) => {
    const st = state[leader], t = st ? st.took : null;
    return { leader, rel, took: t, side: st ? st.side : null, at: st ? st.at : null,
             ok: t == null ? null : (rel === 'yield' ? !t : t) };
  });
}

function App() {
  const [activePage, setActivePage] = useState('dashboard');
  const [activeFeed, setActiveFeed] = useState('UNIFIED');
  const [selected,   setSelected]   = useState(null);
  const [pollPct,    setPollPct]    = useState(0);

  const feedConfig = FEEDS[activeFeed];
  const { decisions, regime, perSystem, online, wsConnected, lastPoll, loading } = useFeed(feedConfig);
  const alert = useAlert();

  // TIER 1 — SL trail update toasts. Fires every time peak_bp moves
  // (i.e. the trail SL shifts up). Uses a stable useCallback so the WS
  // doesn't reconnect on every render.
  const handleTier1Trail = React.useCallback((data) => {
    const label  = data.armed ? 'TRAIL ARMED' : 'TRAIL UPDATE';
    const slFmt  = data.sl_price != null
      ? `$${parseFloat(data.sl_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '—';
    const peakFmt = data.peak_bp != null ? `+${parseFloat(data.peak_bp).toFixed(1)}bp` : '';
    alert.fireAll([{
      system_id:    'TIER1',
      action:       'TRAIL',
      action_label: 'TRAIL',
      signal_ts:    data.bar_ts,
      direction:    (data.side || '').toUpperCase(),
      _trail_label: label,
      _trail_sl:    slFmt,
      _trail_peak:  peakFmt,
      _trail_armed: data.armed,
    }]);
  }, [alert]);

  useTier1TrailAlerts(handleTier1Trail);

  // Alert tracking — only fire for genuinely new decisions after page load.
  // Key includes `action` so a single decision row that transitions from
  // TRADE (entry filled) to EXIT (trade closed) — common pattern for
  // single-row systems like AA4-V4S — fires twice as expected. Without the
  // action segment the second event is silently deduped.
  //
  // Key also includes `signal_id` so two distinct Pine alerts on the same
  // bar (e.g. Trident-exit + Cobra-exit both stamping bar_time=10:21:00)
  // each fire their own toast instead of one swallowing the other.
  const seenKeysRef    = useRef(new Set());
  const initializedRef = useRef(false);

  // TIER strategies put the verdict in `action_label`, not `action`. Use it in
  // the dedup key so a tier ENTRY-then-EXIT row update still fires a new toast
  // (action_label flips from TRADE→EXIT) rather than getting silently deduped.
  const alertKey = (d) => `${d.system_id}-${d.signal_ts}-${d.action_label || d.action}-${d.signal_id || ''}`;

  useEffect(() => {
    if (decisions.length === 0) return;
    if (!initializedRef.current) {
      initializedRef.current = true;
      decisions.forEach(d => seenKeysRef.current.add(alertKey(d)));
      return;
    }
    const newBatch = [];
    for (const d of decisions) {
      const key = alertKey(d);
      if (!seenKeysRef.current.has(key)) {
        seenKeysRef.current.add(key);
        newBatch.push(d);
      }
    }
    if (newBatch.length > 0) {
      // Enrich each new-fired decision with the resolved trigger algo so the
      // toast can show "Cobra + LUX" / "Trident" / etc. without doing the
      // same-bar lookup itself. The full decisions list is the source of
      // truth for the LUX-confluence resolution.
      const triggerIndex = buildTriggerIndex(decisions);
      const enriched = newBatch.map(d => {
        const { trigger, isLuxConfluencing } = resolveTrigger(d, triggerIndex);
        return { ...d, _trigger: trigger, _isLuxConfluencing: isLuxConfluencing,
                 _slotLeaders: slotLeadersFrom(d, decisions) };
      });
      alert.fireAll(enriched);
    }
  }, [decisions, alert]);

  // Reset alert state on tab switch so we don't false-fire on the new feed's history
  useEffect(() => {
    seenKeysRef.current    = new Set();
    initializedRef.current = false;
    setSelected(null);
  }, [activeFeed]);

  // Auto-select newest decision on first load
  useEffect(() => {
    if (!selected && decisions.length > 0) setSelected(decisions[0]);
  }, [decisions, selected]);

  // Poll progress bar
  useEffect(() => {
    let frame;
    const start = Date.now();
    const tick = () => {
      setPollPct(Math.round(((Date.now() - start) % POLL_MS) / POLL_MS * 100));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Hide BLOCK rows from the dashboard's visible lists + counters. BLOCKs
  // are non-actionable audit entries (e.g. cross-tier slot rejections) and
  // belong on the per-tier Monitor pages, not the Portfolio/Strategies feed.
  // `decisions` (unfiltered) is still used for alert dedup tracking above,
  // for trigger-index resolution, and for newest-decision auto-select — all
  // need to see BLOCKs to remain coherent. Only the rendered lists are filtered.
  const visibleDecisions = decisions.filter(
    d => !(d.action_label || d.action || '').startsWith('BLOCK')
  );

  // ── Decision Log ordering: the desk sequencing (strategy_registry run order).
  // Within a day the Decision Log is ordered by the registry sequence (wave/
  // cascade) so it reads top-to-bottom in dependency order. Only the Decision
  // Log uses this; the Feed / stats keep their chronological order. Fetched once.
  const [seqRank, setSeqRank] = useState({});
  const [readsMap, setReadsMap] = useState({});
  useEffect(() => {
    fetch(`${API_BASE}/strategies/api/registry/`, { signal: AbortSignal.timeout(8000) })
      .then(r => r.json())
      .then(d => { if (d && d.ok) {
        const m = {}, reads = {};
        (d.strategies || []).forEach((s, i) => { m[s.name] = i; reads[s.name] = s.reads_upstream || []; });
        setSeqRank(m); setReadsMap(reads);
      }})
      .catch(() => {});
  }, []);
  const _sys = d => (d.system_id || d.system || d.strategy || '').toString().toUpperCase();
  const _day = d => (d.signal_ts || d.session_date || d.decided_at || '').toString().slice(0, 10);
  const _isTrade = d => (d.action === 'TAKE' || d.decision === 'TRADE' || d.decision === 'HOLD' || d.action_label === 'TRADE');
  // Cascade-violation detection: a TRADE by a system whose registry upstream HELD
  // the same day = it should have deferred (e.g. 2026-07-02 JUYI shorts while
  // ANYANWU held a Grade-A long). Build the per-day "held" set, then flag.
  const heldByDay = {};
  visibleDecisions.forEach(d => { if (_isTrade(d)) { const k = _day(d); (heldByDay[k] = heldByDay[k] || new Set()).add(_sys(d)); } });
  const violationOf = d => {
    if (!_isTrade(d)) return null;
    const held = heldByDay[_day(d)]; if (!held) return null;
    const blocker = (readsMap[_sys(d)] || []).find(u => held.has(u));
    return blocker ? `should defer: ${blocker} held` : null;
  };
  // Slot-leader status per decision (feed cards + Decision Log). Uses the shared
  // module-level helper so it stays identical to the toast path.
  const slotLeadersOf = d => slotLeadersFrom(d, visibleDecisions);
  const sequencedDecisions = [...visibleDecisions]
    .map(d => ({ ...d, _cascadeViolation: violationOf(d), _slotLeaders: slotLeadersOf(d) }))
    .sort((a, b) => {
      const da = _day(a), db = _day(b);
      if (da !== db) return da < db ? 1 : -1;                        // newest day first
      const ra = seqRank[_sys(a)] ?? 999, rb = seqRank[_sys(b)] ?? 999;
      if (ra !== rb) return ra - rb;                                 // registry sequence within a day
      return (b.signal_ts || '').localeCompare(a.signal_ts || '');   // newest-first tiebreak
    });

  const featured   = selected || visibleDecisions[0] || decisions[0] || null;
  const isHealthy  = online || wsConnected;
  const showSystem = activeFeed === 'UNIFIED' || activeFeed === 'STAGE0';

  if (activePage === 'ai-review') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <AIReview onClose={() => setActivePage('dashboard')} />
      </div>
    );
  }



  if (activePage === 'kunbi-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <KunbiMonitor />
      </div>
    );
  }

  if (activePage === 'wura-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <WuraMonitor />
      </div>
    );
  }

  if (activePage === 'iyipada-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <IyipadaMonitor />
      </div>
    );
  }

  if (activePage === 'osupa-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <OsupaMonitor />
      </div>
    );
  }

  if (activePage === 'osupa-short-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <OsupaShortMonitor />
      </div>
    );
  }

  if (activePage === 'anyanwu-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <AnyanwuMonitor />
      </div>
    );
  }

  if (activePage === 'iyanu-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <IyanuMonitor />
      </div>
    );
  }

  if (activePage === 'juyi-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <JuyiMonitor />
      </div>
    );
  }

  if (activePage === 'isale-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <IsaleMonitor />
      </div>
    );
  }

  // ── ES Desk monitors (cert ES-DESK-…8392E873) — SHADOW, deployed 2026-06-29 ──
  if (activePage === 'sirius-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <SiriusMonitor />
      </div>
    );
  }

  if (activePage === 'rigel-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <RigelMonitor />
      </div>
    );
  }

  if (activePage === 'mars-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <MarsMonitor />
      </div>
    );
  }

  if (activePage === 'isinmi-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <IsinmiMonitor />
      </div>
    );
  }

  if (activePage === 'saturn-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <SaturnMonitor />
      </div>
    );
  }

  if (activePage === 'venus-monitor') {
    return (
      <div className="app">
        <Navbar feedColor={feedConfig.color} isHealthy={isHealthy} wsConnected={wsConnected} online={online} lastPoll={lastPoll} pollPct={pollPct} alert={alert} activePage={activePage} onPageChange={setActivePage} />
        <VenusMonitor />
      </div>
    );
  }

  if (activePage === 'nuit-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <NuitMonitor />
      </div>
    );
  }

  if (activePage === 'mansa-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <MansaMonitor />
      </div>
    );
  }

  if (activePage === 'sequencing') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <SequencingMap />
      </div>
    );
  }

  // FAVOLA monitor route removed 2026-06-29 (Option A full wipe).

  if (activePage === 'imperial-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <ImperialMonitor />
      </div>
    );
  }

  if (activePage === 'tier1-monitor') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <Tier1Monitor />
      </div>
    );
  }

  // Tier 2 / Tier 5 monitor routes removed 2026-06-16 (tiers removed from app).

  if (activePage === 'system-health') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <SystemHealth />
      </div>
    );
  }

  if (activePage === 'open-positions') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <OpenPositions />
      </div>
    );
  }

  if (activePage === 'backtest') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <Backtest />
      </div>
    );
  }

  if (activePage === 'signal-audit') {
    return (
      <div className="app">
        <Navbar
          feedColor={feedConfig.color}
          isHealthy={isHealthy}
          wsConnected={wsConnected}
          online={online}
          lastPoll={lastPoll}
          pollPct={pollPct}
          alert={alert}
          activePage={activePage}
          onPageChange={setActivePage}
        />
        <SignalAudit onClose={() => setActivePage('dashboard')} />
      </div>
    );
  }

  return (
    <div className="app">
      <Toast
        toasts={alert.toasts}
        onClose={alert.dismissToast}
        onCloseAll={alert.dismissAllToasts}
        onPin={d => setSelected(d)}
      />

      <Navbar
        feedColor={feedConfig.color}
        isHealthy={isHealthy}
        wsConnected={wsConnected}
        online={online}
        lastPoll={lastPoll}
        pollPct={pollPct}
        alert={alert}
        activePage={activePage}
        onPageChange={setActivePage}
      />

      <TabBar activeFeed={activeFeed} onChange={setActiveFeed} />

      <Ticker
        decisions={visibleDecisions}
        feedColor={feedConfig.color}
        perSystem={perSystem}
        showSystem={showSystem}
      />

      <div className="main-body">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-header">
            <span className="sidebar-title">{feedConfig.label} · Feed</span>
            <span style={{ fontSize: 10, color: 'var(--tv-text-muted)', fontFamily: 'Roboto Mono, monospace' }}>
              {visibleDecisions.length}
            </span>
          </div>

          <div className="sidebar-list">
            {loading ? (
              <div className="empty-state">
                <div className="spinner" style={{ borderTopColor: feedConfig.color }} />
                <span style={{ fontSize: 11 }}>Connecting…</span>
              </div>
            ) : visibleDecisions.length === 0 ? (
              <div className="empty-state">
                <span style={{ fontSize: 11 }}>Waiting for signals</span>
              </div>
            ) : (
              visibleDecisions.slice(0, 40).map((d, i) => (
                <SignalCard
                  key={`${d.system_id}-${d.signal_ts}-${i}`}
                  d={d}
                  selected={
                    selected &&
                    selected.signal_ts === d.signal_ts &&
                    selected.system_id === d.system_id
                  }
                  onClick={() => setSelected(d)}
                  showSystem={showSystem}
                  leaders={slotLeadersOf(d)}
                />
              ))
            )}
          </div>

          <div className="sidebar-footer">
            {showSystem && <PerSystemPanel perSystem={perSystem} />}
            <RegimePanel regime={regime} />
          </div>
        </aside>

        {/* Main content */}
        <main className="main-content">
          <StatsRow decisions={visibleDecisions} />
          <ActiveSignals decisions={visibleDecisions} selected={selected} onSelect={setSelected} />
          <FeaturedDecision d={featured} />
          <HistoryTable decisions={sequencedDecisions} showSystem={showSystem} selected={selected} onSelect={setSelected} feedLabel={feedConfig.id.toLowerCase()} />
        </main>
      </div>
    </div>
  );
}

export default App;
